import React, { useState } from "react";
import { format } from "date-fns";

import { ExcelColumnConfig } from "../../types/common";
import { Button, IconButton, Loader } from "../index";
import { DownloadIcon } from "../Icons";

import styles from "./ExportExcelData.module.scss";

interface Props<T> {
  buttonId: string;
  dataRows: T[];
  fileName: string;
  buttonLabel: string;
  isIconButton?: boolean;
  columnConfig?: ExcelColumnConfig<T>[];
  onSuccess?: () => void;
  onError?: (err: any) => void;
  getDataRows?(): Promise<T[]>;
}

const NOOP_FUNC = () => {};

const ExportExcelData = <T,>(props: Props<T>) => {
  const {
    buttonId,
    dataRows,
    fileName,
    buttonLabel,
    isIconButton = false,
    columnConfig,
    onSuccess = NOOP_FUNC,
    onError = NOOP_FUNC,
    getDataRows,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const disabled = isLoading || (!getDataRows && !dataRows.length);

  const exportData = async () => {
    setIsLoading(true);

    try {
      const XLSX = await import("xlsx");
      const uploadedRows = getDataRows ? await getDataRows() : dataRows;

      const mappedRows = columnConfig
        ? uploadedRows.map((row) =>
            columnConfig.reduce((result, column) => {
              const { mappedKey, getOriginalValue } = column;

              result[mappedKey] = getOriginalValue(row);

              return result;
            }, {} as { [key: string]: unknown })
          )
        : uploadedRows;

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(mappedRows);

      if (columnConfig) {
        ws["!cols"] = columnConfig.map((column) => ({ wch: column.width }));
      }

      XLSX.utils.book_append_sheet(wb, ws, "file");
      XLSX.writeFile(
        wb,
        `${fileName}_${format(new Date(), "MM-dd-yyyy")}.xlsx`
      );
      onSuccess();
      setIsLoading(false);
    } catch (err) {
      onError(err);
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader className={styles.inlineLoader} fontSize={20} />}
      {isIconButton ? (
        <IconButton
          hasTooltip
          id={buttonId}
          disabled={disabled}
          onClick={exportData}
          aria-label={buttonLabel}
          className={styles.iconButton}
        >
          <DownloadIcon fillColor={null} />
        </IconButton>
      ) : (
        <Button
          id={buttonId}
          variant="inline"
          disabled={disabled}
          onClick={exportData}
          className={styles.button}
          iconComponent={DownloadIcon}
        >
          {buttonLabel}
        </Button>
      )}
    </>
  );
};

export default ExportExcelData;
